import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Check,
  Eye,
  FileClock,
  Search,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import { activityLogApi } from '@/api/activityLog';
import { Badge, Button, EmptyState, Loading, Modal } from '@/components/ui';
import type {
  ActivityAlert,
  ActivityAlertRule,
  ActivityLogFilters,
  ActivityLogRow,
  ActivitySeverity,
  ActivitySource,
  AdminActionLogRow,
  LoginEvent,
  LoginLogFilters,
  LoginLogRow,
} from '@/types/activityLog';
import { formatDateTime } from '@/utils/date';

type TabId = 'log' | 'alerts' | 'admin';
type LogSource = 'activity' | 'login';
type SelectedRow =
  | { kind: 'activity'; row: ActivityLogRow }
  | { kind: 'login'; row: LoginLogRow }
  | { kind: 'admin'; row: AdminActionLogRow };

const SEVERITIES: ActivitySeverity[] = ['info', 'notice', 'warning', 'critical'];
const LOGIN_EVENTS: LoginEvent[] = [
  'login_success',
  'login_failed',
  'logout',
  'password_reset',
  'mfa_change',
];

const SEVERITY_LABELS: Record<ActivitySeverity, string> = {
  info: 'מידע',
  notice: 'לתשומת לב',
  warning: 'אזהרה',
  critical: 'קריטי',
};

const LOGIN_EVENT_LABELS: Record<LoginEvent, string> = {
  login_success: 'כניסה הצליחה',
  login_failed: 'כניסה נכשלה',
  logout: 'יציאה',
  password_reset: 'איפוס סיסמה',
  mfa_change: 'שינוי אימות דו־שלבי',
};

const ACTIVITY_SOURCE_LABELS: Record<ActivitySource, string> = {
  app: 'אפליקציה',
  edge_fn: 'פונקציית קצה',
  db_trigger: 'טריגר מסד נתונים',
  api_v1: 'API',
  portal: 'פורטל',
};

const ADMIN_SOURCE_LABELS: Record<AdminActionLogRow['source_kind'], string> = {
  admin_audit_log: 'יומן מנהל',
  admin_impersonation_log: 'יומן התחזות',
};

const inputClass =
  'h-10 w-full rounded-lg border border-navy-border bg-navy-card px-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-indigo-500/40';

function clean(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

function toIso(value: string): string | undefined {
  return value ? new Date(value).toISOString() : undefined;
}

function toDateTimeLocal(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function exclusiveUpperBound(value: string): string {
  return new Date(new Date(value).getTime() + 1).toISOString();
}

function severityVariant(
  severity: ActivitySeverity,
): 'danger' | 'warning' | 'info' | 'neutral' {
  if (severity === 'critical') return 'danger';
  if (severity === 'warning') return 'warning';
  if (severity === 'notice') return 'info';
  return 'neutral';
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre
      dir="ltr"
      className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-navy-border bg-navy/40 p-3 text-left text-xs text-ink-secondary"
    >
      {JSON.stringify(value ?? {}, null, 2)}
    </pre>
  );
}

function ErrorCard({ error }: { error: Error }) {
  return (
    <div role="alert" className="card border border-danger/30 p-4 text-sm text-danger">
      שגיאה בטעינת הנתונים: {error.message}
    </div>
  );
}

function Kpis() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['activity-kpis'],
    queryFn: activityLogApi.getKpis,
    staleTime: 30_000,
  });

  if (error) return <ErrorCard error={error as Error} />;

  const cards = [
    { label: 'פעולות ב־24 השעות האחרונות', value: data?.actions_24h, icon: Activity },
    { label: 'מחיקות ב־24 השעות האחרונות', value: data?.deletes_24h, icon: Trash2 },
    { label: 'אירועי חסימת גישה ב־24 שעות', value: data?.access_denied_24h, icon: ShieldAlert },
    { label: 'התראות פתוחות', value: data?.open_alerts, icon: AlertTriangle },
  ];

  return (
    <div className="responsive-grid-4">
      {cards.map(({ label, value, icon: Icon }) => (
        <div key={label} className="card flex items-center gap-4 p-4">
          <div className="rounded-lg bg-indigo-500/15 p-3 text-indigo-300">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <p className="text-xs text-ink-secondary">{label}</p>
            <p className="mt-1 text-2xl font-bold text-ink-primary">
              {isLoading ? '…' : Number(value ?? 0).toLocaleString('he-IL')}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

interface LogPanelProps {
  source: LogSource;
  setSource: (source: LogSource) => void;
  activityFilters: ActivityLogFilters;
  setActivityFilters: (filters: ActivityLogFilters) => void;
  onSelect: (row: SelectedRow) => void;
}

interface ActivityFilterDraft {
  orgId: string;
  userId: string;
  action: string;
  entityType: string;
  severity: ActivitySeverity | '';
  fromDate: string;
  toDate: string;
  search: string;
}

interface LoginFilterDraft {
  orgId: string;
  userId: string;
  event: LoginEvent | '';
  fromDate: string;
  toDate: string;
  search: string;
}

function LogPanel({
  source,
  setSource,
  activityFilters,
  setActivityFilters,
  onSelect,
}: LogPanelProps) {
  const [activityDraft, setActivityDraft] = useState<ActivityFilterDraft>({
    orgId: activityFilters.orgId ?? '',
    userId: activityFilters.userId ?? '',
    action: activityFilters.action ?? '',
    entityType: activityFilters.entityType ?? '',
    severity: activityFilters.severity ?? '',
    fromDate: toDateTimeLocal(activityFilters.fromTs),
    toDate: toDateTimeLocal(activityFilters.toTs),
    search: activityFilters.search ?? '',
  });
  const [loginDraft, setLoginDraft] = useState<LoginFilterDraft>({
    orgId: '',
    userId: '',
    event: '',
    fromDate: '',
    toDate: '',
    search: '',
  });
  const [loginFilters, setLoginFilters] = useState<LoginLogFilters>({});
  const activitySourceRef = useRef<HTMLButtonElement>(null);
  const loginSourceRef = useRef<HTMLButtonElement>(null);

  const activityQuery = useInfiniteQuery({
    queryKey: ['activity-log', activityFilters],
    queryFn: ({ pageParam }) =>
      activityLogApi.getActivityLog(activityFilters, pageParam),
    initialPageParam: null as { afterTs: string; afterId: string } | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: source === 'activity',
  });

  const loginQuery = useInfiniteQuery({
    queryKey: ['login-log', loginFilters],
    queryFn: ({ pageParam }) => activityLogApi.getLoginLog(loginFilters, pageParam),
    initialPageParam: null as { afterTs: string; afterId: string } | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: source === 'login',
  });

  const applyFilters = () => {
    if (source === 'activity') {
      setActivityFilters({
        orgId: clean(activityDraft.orgId),
        userId: clean(activityDraft.userId),
        action: clean(activityDraft.action),
        actionPattern: clean(activityDraft.action)
          ? undefined
          : activityFilters.actionPattern,
        entityType: clean(activityDraft.entityType),
        severity: activityDraft.severity || undefined,
        fromTs: toIso(activityDraft.fromDate),
        toTs: toIso(activityDraft.toDate),
        search: clean(activityDraft.search),
      });
    } else {
      setLoginFilters({
        orgId: clean(loginDraft.orgId),
        userId: clean(loginDraft.userId),
        event: loginDraft.event || undefined,
        fromTs: toIso(loginDraft.fromDate),
        toTs: toIso(loginDraft.toDate),
        search: clean(loginDraft.search),
      });
    }
  };

  const resetFilters = () => {
    if (source === 'activity') {
      setActivityDraft({
        orgId: '',
        userId: '',
        action: '',
        entityType: '',
        severity: '',
        fromDate: '',
        toDate: '',
        search: '',
      });
      setActivityFilters({});
    } else {
      setLoginDraft({
        orgId: '',
        userId: '',
        event: '',
        fromDate: '',
        toDate: '',
        search: '',
      });
      setLoginFilters({});
    }
  };

  const query = source === 'activity' ? activityQuery : loginQuery;
  const activityRows =
    source === 'activity'
      ? activityQuery.data?.pages.flatMap((page) => page.items) ?? []
      : [];
  const loginRows =
    source === 'login'
      ? loginQuery.data?.pages.flatMap((page) => page.items) ?? []
      : [];

  const handleSourceKey = (
    event: KeyboardEvent<HTMLButtonElement>,
    current: LogSource,
  ) => {
    let next: LogSource | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = current === 'activity' ? 'login' : 'activity';
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = current === 'activity' ? 'login' : 'activity';
    } else if (event.key === 'Home') {
      next = 'activity';
    } else if (event.key === 'End') {
      next = 'login';
    }
    if (!next) return;
    event.preventDefault();
    setSource(next);
    (next === 'activity' ? activitySourceRef : loginSourceRef).current?.focus();
  };

  return (
    <div className="space-y-4">
      <div
        role="radiogroup"
        className="inline-flex rounded-lg border border-navy-border bg-navy-card p-1"
        aria-label="בחירת מקור יומן"
      >
        <button
          ref={activitySourceRef}
          id="activity-source-activity"
          type="button"
          role="radio"
          aria-checked={source === 'activity'}
          tabIndex={source === 'activity' ? 0 : -1}
          onClick={() => setSource('activity')}
          onKeyDown={(event) => handleSourceKey(event, 'activity')}
          className={`rounded-md px-4 py-2 text-sm transition-colors ${
            source === 'activity' ? 'bg-indigo-600 text-white' : 'text-ink-secondary'
          }`}
        >
          פעילות
        </button>
        <button
          ref={loginSourceRef}
          id="activity-source-login"
          type="button"
          role="radio"
          aria-checked={source === 'login'}
          tabIndex={source === 'login' ? 0 : -1}
          onClick={() => setSource('login')}
          onKeyDown={(event) => handleSourceKey(event, 'login')}
          className={`rounded-md px-4 py-2 text-sm transition-colors ${
            source === 'login' ? 'bg-indigo-600 text-white' : 'text-ink-secondary'
          }`}
        >
          כניסות
        </button>
      </div>

      <form
        className="card space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <label className="text-xs text-ink-secondary">
            ארגון
            <input
              value={source === 'activity' ? activityDraft.orgId : loginDraft.orgId}
              onChange={(event) =>
                source === 'activity'
                  ? setActivityDraft({ ...activityDraft, orgId: event.target.value })
                  : setLoginDraft({ ...loginDraft, orgId: event.target.value })
              }
              className={`${inputClass} mt-1`}
              placeholder="מזהה ארגון"
            />
          </label>
          <label className="text-xs text-ink-secondary">
            משתמש
            <input
              value={source === 'activity' ? activityDraft.userId : loginDraft.userId}
              onChange={(event) =>
                source === 'activity'
                  ? setActivityDraft({ ...activityDraft, userId: event.target.value })
                  : setLoginDraft({ ...loginDraft, userId: event.target.value })
              }
              className={`${inputClass} mt-1`}
              placeholder="מזהה משתמש"
            />
          </label>
          {source === 'activity' ? (
            <>
              <label className="text-xs text-ink-secondary">
                פעולה
                <input
                  value={activityDraft.action}
                  onChange={(event) =>
                    setActivityDraft({ ...activityDraft, action: event.target.value })
                  }
                  className={`${inputClass} mt-1`}
                  placeholder="לדוגמה: delete"
                />
              </label>
              <label className="text-xs text-ink-secondary">
                ישות
                <input
                  value={activityDraft.entityType}
                  onChange={(event) =>
                    setActivityDraft({ ...activityDraft, entityType: event.target.value })
                  }
                  className={`${inputClass} mt-1`}
                  placeholder="סוג ישות"
                />
              </label>
              <label className="text-xs text-ink-secondary">
                חומרה
                <select
                  value={activityDraft.severity}
                  onChange={(event) =>
                    setActivityDraft({
                      ...activityDraft,
                      severity: event.target.value as ActivitySeverity | '',
                    })
                  }
                  className={`${inputClass} mt-1`}
                >
                  <option value="">הכול</option>
                  {SEVERITIES.map((value) => (
                    <option key={value} value={value}>
                      {SEVERITY_LABELS[value]}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <label className="text-xs text-ink-secondary">
              אירוע
              <select
                value={loginDraft.event}
                onChange={(event) =>
                  setLoginDraft({
                    ...loginDraft,
                    event: event.target.value as LoginEvent | '',
                  })
                }
                className={`${inputClass} mt-1`}
              >
                <option value="">הכול</option>
                {LOGIN_EVENTS.map((value) => (
                  <option key={value} value={value}>
                    {LOGIN_EVENT_LABELS[value]}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-xs text-ink-secondary">
            מתאריך
            <input
              type="datetime-local"
              value={source === 'activity' ? activityDraft.fromDate : loginDraft.fromDate}
              onChange={(event) =>
                source === 'activity'
                  ? setActivityDraft({ ...activityDraft, fromDate: event.target.value })
                  : setLoginDraft({ ...loginDraft, fromDate: event.target.value })
              }
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-xs text-ink-secondary">
            עד תאריך
            <input
              type="datetime-local"
              value={source === 'activity' ? activityDraft.toDate : loginDraft.toDate}
              onChange={(event) =>
                source === 'activity'
                  ? setActivityDraft({ ...activityDraft, toDate: event.target.value })
                  : setLoginDraft({ ...loginDraft, toDate: event.target.value })
              }
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-xs text-ink-secondary sm:col-span-2">
            חיפוש חופשי
            <div className="relative mt-1">
              <Search
                className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                aria-hidden="true"
              />
              <input
                value={source === 'activity' ? activityDraft.search : loginDraft.search}
                onChange={(event) =>
                  source === 'activity'
                    ? setActivityDraft({ ...activityDraft, search: event.target.value })
                    : setLoginDraft({ ...loginDraft, search: event.target.value })
                }
                className={`${inputClass} pr-9`}
                placeholder="דוא״ל, תווית, כתובת IP או פרטים נוספים"
              />
            </div>
          </label>
        </div>
        {source === 'activity' && activityFilters.actionPattern ? (
          <p className="text-xs text-indigo-300">
            תבנית פעולה פעילה: <span dir="ltr">{activityFilters.actionPattern}</span>
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" size="sm">
            החלת מסננים
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={resetFilters}>
            איפוס
          </Button>
        </div>
      </form>

      {query.error ? (
        <ErrorCard error={query.error as Error} />
      ) : query.isLoading ? (
        <Loading text="טוען יומן פעילות..." className="py-12" />
      ) : source === 'activity' && activityRows.length ? (
        <ActivityTable rows={activityRows} onSelect={onSelect} />
      ) : source === 'login' && loginRows.length ? (
        <LoginTable rows={loginRows} onSelect={onSelect} />
      ) : (
        <EmptyState
          icon={<FileClock className="h-8 w-8" />}
          title="לא נמצאו אירועים"
          description="לא נמצאו רשומות התואמות למסננים שנבחרו."
        />
      )}

      {query.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            onClick={() => query.fetchNextPage()}
            loading={query.isFetchingNextPage}
          >
            טעינת תוצאות נוספות
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ActivityTable({
  rows,
  onSelect,
}: {
  rows: ActivityLogRow[];
  onSelect: (row: SelectedRow) => void;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="responsive-table-wrapper">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-navy-border bg-navy/30">
              {['מועד', 'ארגון / משתמש', 'פעולה', 'ישות / תווית', 'חומרה', 'מקור'].map(
                (label) => (
                  <th key={label} className="px-4 py-3 text-start text-xs text-ink-muted">
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-border">
            {rows.map((row) => (
              <tr
                key={`${row.occurred_at}:${row.id}`}
                role="button"
                tabIndex={0}
                aria-label={`פתיחת פרטי פעילות: ${row.action}, ${row.entity_label ?? row.entity_type}, ${formatDateTime(row.occurred_at)}`}
                onClick={() => onSelect({ kind: 'activity', row })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    if (event.key === ' ') event.preventDefault();
                    onSelect({ kind: 'activity', row });
                  }
                }}
                className="cursor-pointer transition-colors hover:bg-navy/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              >
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {formatDateTime(row.occurred_at)}
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="text-ink-primary">{row.org_id ?? '—'}</div>
                  <div className="text-ink-muted">
                    {row.actor_email ?? row.actor_user_id ?? '—'}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-ink-primary">{row.action}</td>
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {row.entity_type}
                  {row.entity_label ? ` · ${row.entity_label}` : ''}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={severityVariant(row.severity)}>
                    {SEVERITY_LABELS[row.severity]}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted">
                  {ACTIVITY_SOURCE_LABELS[row.source]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LoginTable({
  rows,
  onSelect,
}: {
  rows: LoginLogRow[];
  onSelect: (row: SelectedRow) => void;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="responsive-table-wrapper">
        <table className="w-full min-w-[850px] text-sm">
          <thead>
            <tr className="border-b border-navy-border bg-navy/30">
              {['מועד', 'משתמש / דוא״ל', 'אירוע', 'כתובת IP', 'IP חדש', 'דפדפן'].map(
                (label) => (
                  <th key={label} className="px-4 py-3 text-start text-xs text-ink-muted">
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-border">
            {rows.map((row) => (
              <tr
                key={`${row.occurred_at}:${row.id}`}
                role="button"
                tabIndex={0}
                aria-label={`פתיחת פרטי כניסה: ${LOGIN_EVENT_LABELS[row.event]}, ${row.email ?? row.user_id ?? 'משתמש לא מזוהה'}, ${formatDateTime(row.occurred_at)}`}
                onClick={() => onSelect({ kind: 'login', row })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    if (event.key === ' ') event.preventDefault();
                    onSelect({ kind: 'login', row });
                  }
                }}
                className="cursor-pointer transition-colors hover:bg-navy/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              >
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {formatDateTime(row.occurred_at)}
                </td>
                <td className="px-4 py-3 text-xs text-ink-primary">{row.email ?? row.user_id ?? '—'}</td>
                <td className="px-4 py-3 text-xs text-ink-primary">
                  {LOGIN_EVENT_LABELS[row.event]}
                </td>
                <td dir="ltr" className="px-4 py-3 text-left font-mono text-xs text-ink-secondary">
                  {row.ip_address ?? '—'}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={row.is_new_ip ? 'warning' : 'neutral'}>
                    {row.is_new_ip ? 'כן' : 'לא'}
                  </Badge>
                </td>
                <td className="max-w-xs truncate px-4 py-3 text-xs text-ink-muted">
                  {row.user_agent ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AlertRuleEditor({
  rule,
  onSaved,
}: {
  rule: ActivityAlertRule;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(() => JSON.stringify(rule.threshold, null, 2));
  const [enabled, setEnabled] = useState(rule.enabled);
  const [parseError, setParseError] = useState('');
  const mutation = useMutation({
    mutationFn: (threshold: Record<string, unknown>) =>
      activityLogApi.updateAlertRule(rule.rule_key, threshold, enabled),
    onSuccess: onSaved,
  });

  const save = () => {
    try {
      const parsed = JSON.parse(draft);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('הסף חייב להיות אובייקט JSON.');
      }
      setParseError('');
      mutation.mutate(parsed as Record<string, unknown>);
    } catch (error) {
      setParseError((error as Error).message);
    }
  };

  return (
    <article className="rounded-lg border border-navy-border bg-navy/20 p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-medium text-ink-primary">{rule.name_he}</h4>
          <p dir="ltr" className="text-left text-xs text-ink-muted">
            {rule.rule_key}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-secondary">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            className="h-4 w-4 accent-indigo-500"
          />
          כלל פעיל
        </label>
      </div>
      <label className="text-xs text-ink-secondary">
        הגדרת סף
        <textarea
          dir="ltr"
          rows={3}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-label={`סף להתראה ${rule.name_he}`}
          className="mt-1 w-full rounded-lg border border-navy-border bg-navy/40 p-2 text-left font-mono text-xs text-ink-primary focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
        />
      </label>
      {parseError || mutation.error ? (
        <p role="alert" className="mt-1 text-xs text-danger">
          {parseError || (mutation.error as Error).message}
        </p>
      ) : null}
      <Button size="sm" variant="secondary" onClick={save} loading={mutation.isPending}>
        שמירת סף
      </Button>
    </article>
  );
}

function AlertsPanel({
  onShowInLog,
}: {
  onShowInLog: (alert: ActivityAlert) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['activity-alerts'],
    queryFn: activityLogApi.getAlerts,
    staleTime: 15_000,
  });
  const rulesQuery = useQuery({
    queryKey: ['activity-alert-rules'],
    queryFn: activityLogApi.getAlertRules,
    staleTime: 15_000,
  });
  const statusMutation = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: 'ack' | 'dismissed';
    }) => activityLogApi.updateAlertStatus(id, status),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['activity-alerts'] }),
        queryClient.invalidateQueries({ queryKey: ['activity-kpis'] }),
      ]);
    },
  });

  const refreshRules = () =>
    queryClient.invalidateQueries({ queryKey: ['activity-alert-rules'] });

  if (error) return <ErrorCard error={error as Error} />;
  if (isLoading) return <Loading text="טוען התראות..." className="py-12" />;

  return (
    <div className="space-y-6">
      <section className="card p-4" aria-labelledby="alert-rules-heading">
        <h3 id="alert-rules-heading" className="mb-3 text-lg font-semibold text-ink-primary">
          כללי התראה
        </h3>
        {rulesQuery.error ? (
          <ErrorCard error={rulesQuery.error as Error} />
        ) : rulesQuery.isLoading ? (
          <Loading text="טוען כללי התראה..." className="py-8" />
        ) : rulesQuery.data?.length ? (
          <div className="grid gap-3 xl:grid-cols-2">
            {rulesQuery.data.map((rule) => (
              <AlertRuleEditor
                key={`${rule.id}:${rule.updated_at}`}
                rule={rule}
                onSaved={refreshRules}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="אין כללי התראה"
            description="לא נמצאו כללים לעריכה."
          />
        )}
      </section>

      <section className="space-y-3" aria-labelledby="activity-alerts-heading">
        <h3 id="activity-alerts-heading" className="text-lg font-semibold text-ink-primary">
          התראות שזוהו
        </h3>
      {statusMutation.error ? <ErrorCard error={statusMutation.error as Error} /> : null}
      {!data?.length ? (
        <EmptyState
          icon={<AlertTriangle className="h-8 w-8" />}
          title="אין התראות"
          description="לא נמצאו התראות פעילות או היסטוריות."
        />
      ) : data.map((alert) => (
        <article key={alert.id} className="card p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-ink-primary">{alert.name_he}</h3>
                <Badge variant={severityVariant(alert.severity)}>
                  {SEVERITY_LABELS[alert.severity]}
                </Badge>
                <Badge variant={alert.status === 'new' ? 'warning' : 'neutral'}>
                  {alert.status === 'new' ? 'חדשה' : alert.status === 'ack' ? 'אושרה' : 'נדחתה'}
                </Badge>
              </div>
              <p className="mt-2 text-xs text-ink-secondary">
                {formatDateTime(alert.created_at)} ·{' '}
                {alert.org_id ?? 'ללא ארגון'} · {alert.user_id ?? 'ללא משתמש'}
              </p>
              <p className="mt-1 text-xs text-ink-muted">
                חלון: {formatDateTime(alert.window_start)} – {formatDateTime(alert.window_end)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => onShowInLog(alert)}>
                <Eye className="h-4 w-4" />
                הצגה ביומן
              </Button>
              {alert.status === 'new' ? (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={
                      statusMutation.isPending &&
                      statusMutation.variables?.id === alert.id &&
                      statusMutation.variables.status === 'ack'
                    }
                    disabled={
                      statusMutation.isPending &&
                      statusMutation.variables?.id === alert.id
                    }
                    onClick={() => statusMutation.mutate({ id: alert.id, status: 'ack' })}
                  >
                    <Check className="h-4 w-4" />
                    אישור
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={
                      statusMutation.isPending &&
                      statusMutation.variables?.id === alert.id &&
                      statusMutation.variables.status === 'dismissed'
                    }
                    disabled={
                      statusMutation.isPending &&
                      statusMutation.variables?.id === alert.id
                    }
                    onClick={() =>
                      statusMutation.mutate({ id: alert.id, status: 'dismissed' })
                    }
                  >
                    <X className="h-4 w-4" />
                    דחייה
                  </Button>
                </>
              ) : null}
            </div>
          </div>
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-indigo-300">ראיות</summary>
            <div className="mt-2">
              <JsonBlock value={alert.evidence} />
            </div>
          </details>
        </article>
      ))}
      </section>
    </div>
  );
}

function AdminActionsPanel({
  onSelect,
}: {
  onSelect: (row: SelectedRow) => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['activity-admin-actions'],
    queryFn: activityLogApi.getAdminActions,
    staleTime: 15_000,
  });

  if (error) return <ErrorCard error={error as Error} />;
  if (isLoading) return <Loading text="טוען פעולות מנהל..." className="py-12" />;
  if (!data?.length) {
    return (
      <EmptyState
        icon={<ShieldAlert className="h-8 w-8" />}
        title="אין פעולות מנהל"
        description="לא נמצאו רשומות ביומני המנהל וההתחזות."
      />
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="responsive-table-wrapper">
        <table className="w-full min-w-[850px] text-sm">
          <thead>
            <tr className="border-b border-navy-border bg-navy/30">
              {['מועד', 'מנהל', 'פעולה', 'ארגון', 'משתמש יעד', 'מקור'].map((label) => (
                <th key={label} className="px-4 py-3 text-start text-xs text-ink-muted">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-border">
            {data.map((row) => (
              <tr
                key={`${row.source_kind}:${row.id}`}
                role="button"
                tabIndex={0}
                aria-label={`פתיחת פרטי פעולת מנהל: ${row.action}, ${row.actor_email ?? 'מנהל לא מזוהה'}, ${formatDateTime(row.occurred_at)}`}
                onClick={() => onSelect({ kind: 'admin', row })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    if (event.key === ' ') event.preventDefault();
                    onSelect({ kind: 'admin', row });
                  }
                }}
                className="cursor-pointer transition-colors hover:bg-navy/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              >
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {formatDateTime(row.occurred_at)}
                </td>
                <td className="px-4 py-3 text-xs text-ink-primary">{row.actor_email ?? '—'}</td>
                <td className="px-4 py-3 text-xs text-ink-primary">{row.action}</td>
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {row.org_id ?? '—'}
                </td>
                <td className="px-4 py-3 text-xs text-ink-secondary">
                  {row.target_user_id ?? '—'}
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted">
                  {ADMIN_SOURCE_LABELS[row.source_kind]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RowDetails({
  selected,
  onClose,
}: {
  selected: SelectedRow | null;
  onClose: () => void;
}) {
  const row = selected?.row;
  const metadata = row?.metadata ?? {};
  const before = metadata.before;
  const after = metadata.after;

  return (
    <Modal
      isOpen={!!selected}
      onClose={onClose}
      title="פרטי אירוע"
      size="lg"
    >
      {selected && row ? (
        <div dir="rtl" className="space-y-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs text-ink-muted">מועד</p>
              <p className="text-ink-primary">{formatDateTime(row.occurred_at)}</p>
            </div>
            <div>
              <p className="text-xs text-ink-muted">סוג</p>
              <p className="text-ink-primary">
                {selected.kind === 'activity'
                  ? selected.row.action
                  : selected.kind === 'login'
                    ? LOGIN_EVENT_LABELS[selected.row.event]
                    : selected.row.action}
              </p>
            </div>
          </div>
          {before !== undefined || after !== undefined ? (
            <div className="grid gap-3 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs text-ink-muted">לפני</p>
                <JsonBlock value={before} />
              </div>
              <div>
                <p className="mb-2 text-xs text-ink-muted">אחרי</p>
                <JsonBlock value={after} />
              </div>
            </div>
          ) : null}
          <div>
            <p className="mb-2 text-xs text-ink-muted">מטא־דאטה</p>
            <JsonBlock value={metadata} />
          </div>
          {'user_agent' in row ? (
            <div>
              <p className="text-xs text-ink-muted">דפדפן</p>
              <p dir="ltr" className="break-all text-left text-xs text-ink-secondary">
                {row.user_agent ?? '—'}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

export default function ActivityLogPage() {
  const [tab, setTab] = useState<TabId>('log');
  const [source, setSource] = useState<LogSource>('activity');
  const [activityFilters, setActivityFilters] = useState<ActivityLogFilters>({});
  const [selected, setSelected] = useState<SelectedRow | null>(null);

  const tabs = useMemo(
    () => [
      { id: 'log' as const, label: 'יומן', icon: FileClock },
      { id: 'alerts' as const, label: 'התראות', icon: AlertTriangle },
      { id: 'admin' as const, label: 'פעולות מנהל', icon: ShieldAlert },
    ],
    [],
  );

  const showAlertInLog = (alert: ActivityAlert) => {
    const action =
      typeof alert.evidence.action === 'string' ? alert.evidence.action : undefined;
    setActivityFilters({
      orgId: alert.org_id ?? undefined,
      userId: alert.user_id ?? undefined,
      action,
      actionPattern: alert.rule_key === 'mass_delete' ? '%delete%' : undefined,
      fromTs: alert.window_start,
      toTs: exclusiveUpperBound(alert.window_end),
    });
    setSource('activity');
    setTab('log');
  };

  const handleTabKey = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    }
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    setTab(nextTab.id);
    requestAnimationFrame(() => {
      document.getElementById(`activity-tab-${nextTab.id}`)?.focus();
    });
  };

  return (
    <div dir="rtl" className="space-y-6 text-right">
      <div>
        <h1 className="text-2xl font-bold text-ink-primary">יומן פעילות</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          מעקב מרכזי אחר פעילות משתמשים, כניסות, התראות ופעולות מנהל.
        </p>
      </div>

      <Kpis />

      <div
        role="tablist"
        aria-label="תצוגות יומן פעילות"
        className="flex gap-2 overflow-x-auto border-b border-navy-border"
      >
        {tabs.map(({ id, label, icon: Icon }, index) => (
          <button
            key={id}
            id={`activity-tab-${id}`}
            type="button"
            role="tab"
            aria-selected={tab === id}
            aria-controls={`activity-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => setTab(id)}
            onKeyDown={(event) => handleTabKey(event, index)}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              tab === id
                ? 'border-indigo-500 text-ink-primary'
                : 'border-transparent text-ink-secondary hover:text-ink-primary'
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      <div
        id={`activity-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`activity-tab-${tab}`}
      >
        {tab === 'log' ? (
          <LogPanel
            source={source}
            setSource={setSource}
            activityFilters={activityFilters}
            setActivityFilters={setActivityFilters}
            onSelect={setSelected}
          />
        ) : tab === 'alerts' ? (
          <AlertsPanel onShowInLog={showAlertInLog} />
        ) : (
          <AdminActionsPanel onSelect={setSelected} />
        )}
      </div>

      <RowDetails selected={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
